/**
 * ChatPanel — platform-native chat iframe shown right of the player when a
 * creator is live. The URL comes from `getChatEmbedUrl` in shared.tsx, the one
 * chat-embed builder. Hidden entirely when offline — there's no live chat to
 * show for a VOD.
 */

import React, { useEffect, useRef, useState } from "react";
import { getChatEmbedUrl, type StreamingSource } from "./shared";

interface ChatPanelProps {
    platform: StreamingSource;
    twitchChannelId?: string;
    kickChannelSlug?: string;
    youtubeLiveId?: string;
}

/*
 * Note: A previous version also exported a `chatPopoutUrl()` escape hatch
 * link for the chat panel. It was removed — the StreamInlinePlayer popout
 * opens the same stream URL where chat is built in, so a duplicate chat
 * popout was redundant. If the chat embed gets gated by content
 * classification (separate from the video gate), the player popout is
 * still the single escape hatch.
 */

export const ChatPanel: React.FC<ChatPanelProps> = (props) => {
    const url = getChatEmbedUrl(props);
    const wrapperRef = useRef<HTMLElement | null>(null);
    /*
     * Defer chat-iframe mount until on-screen via IntersectionObserver —
     * same rationale as StreamInlinePlayer: avoid burning Kasada anti-bot
     * challenges on hidden tabs. Latches once visible.
     */
    const [hasBeenVisible, setHasBeenVisible] = useState(false);

    useEffect(() => {
        if (hasBeenVisible) return;
        const el = wrapperRef.current;
        if (!el) return;
        const observer = new IntersectionObserver(entries => {
            if (entries.some(e => e.isIntersecting)) {
                setHasBeenVisible(true);
                observer.disconnect();
            }
        }, {threshold: 0.01});
        observer.observe(el);
        return () => observer.disconnect();
    }, [hasBeenVisible]);

    if (!url) return null;
    return (
        <aside className="hcs-chat-panel" aria-label="Live chat" ref={wrapperRef}>
            {hasBeenVisible && <iframe title="Live chat" src={url} frameBorder={0} loading="lazy" />}
        </aside>
    );
};
